/**
 * The objects of one kind under a root, kept in a flat list for a pass that runs every render. The
 * first read walks the root. After that, only a subtree three reports added or removed below the
 * root is walked, so no render and no arrival traverses the whole scene.
 */
export class TrackedObjects {
    root;
    isTracked;
    /** Edited in place by `track` and `untrack`, so the list a caller holds stays the current one. */
    tracked = [];
    /** Each object's position in `tracked`, so a removal swaps with the last entry in O(1). */
    trackedIndex = new Map();
    /** Every object that carries this tracker's two listeners. */
    watched = new Set();
    /** Changes whenever an object enters or leaves the list, so a caller skips a frame that changed none. */
    version = 0;
    /** Set by the first `current()`, cleared by `dispose`. */
    hasWalkedRoot = false;
    onChildAdded = (event) => this.track(event.child);
    onChildRemoved = (event) => this.untrack(event.child);
    /** `isTracked` must answer the same for an object for as long as it is under the root. */
    constructor(root, isTracked) {
        this.root = root;
        this.isTracked = isTracked;
    }
    /**
     * The tracked objects under the root, in no particular order. `Object3D.add`, `attach` and `remove`
     * all dispatch `childadded` or `childremoved` on the parent (`Object3D.js`), and so does R3F's
     * own `insertBefore`, so the list follows every change without a walk of the root.
     */
    current() {
        if (!this.hasWalkedRoot) {
            this.track(this.root);
            this.hasWalkedRoot = true;
        }
        return this.tracked;
    }
    /** Removes every listener. A later `current()` walks the root afresh. */
    dispose() {
        for (const object of this.watched)
            this.unwatch(object);
        this.watched.clear();
        this.tracked.length = 0;
        this.trackedIndex.clear();
        this.hasWalkedRoot = false;
        this.version++;
    }
    track(subtree) {
        subtree.traverse((object) => {
            if (this.watched.has(object))
                return;
            this.watched.add(object);
            object.addEventListener('childadded', this.onChildAdded);
            object.addEventListener('childremoved', this.onChildRemoved);
            if (!this.isTracked(object))
                return;
            this.trackedIndex.set(object, this.tracked.length);
            this.tracked.push(object);
            this.version++;
        });
    }
    untrack(subtree) {
        subtree.traverse((object) => {
            if (!this.watched.delete(object))
                return;
            this.unwatch(object);
            this.removeTracked(object);
        });
    }
    removeTracked(object) {
        const index = this.trackedIndex.get(object);
        if (index === undefined)
            return;
        this.trackedIndex.delete(object);
        this.version++;
        const last = this.tracked.pop();
        if (last === object)
            return;
        this.tracked[index] = last;
        this.trackedIndex.set(last, index);
    }
    unwatch(object) {
        object.removeEventListener('childadded', this.onChildAdded);
        object.removeEventListener('childremoved', this.onChildRemoved);
    }
}
//# sourceMappingURL=trackedObjects.js.map